import { type RefObject, useEffect } from 'react';

const HEADER_HEIGHT = 64;
const BOTTOM_OFFSET = 166;
const CARD_MIN_HEIGHT = 312;
const CARD_MAX_HEIGHT = 480;
const CARD_LANDSCAPE_MIN_HEIGHT = 160;
const CARD_LANDSCAPE_MAX_HEIGHT = 280;

const useInterfaceCardHeight = (
  containerRef: RefObject<HTMLElement | null>,
  drawerHeight = 0,
  landscape = false,
  isOpen = false
) => {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const updateCardHeight = () => {
      const container = containerRef.current;

      if (!container) {
        return;
      }

      const { minLimit, maxLimit, offset } = landscape
        ? { minLimit: CARD_LANDSCAPE_MIN_HEIGHT, maxLimit: CARD_LANDSCAPE_MAX_HEIGHT, offset: 0 }
        : { minLimit: CARD_MIN_HEIGHT, maxLimit: CARD_MAX_HEIGHT, offset: drawerHeight };

      const available = window.innerHeight - HEADER_HEIGHT - BOTTOM_OFFSET - offset;
      const maxHeight = Math.min(maxLimit, Math.max(available, 0));
      const minHeight = Math.min(minLimit, maxHeight);

      container.style.setProperty('--interface-card-min-h', `${minHeight}px`);
      container.style.setProperty('--interface-card-max-h', `${maxHeight}px`);
    };

    let frame = requestAnimationFrame(updateCardHeight);

    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateCardHeight);
    };

    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);

      const container = containerRef.current;

      if (container && container.offsetParent === null) {
        container.style.removeProperty('--interface-card-min-h');
        container.style.removeProperty('--interface-card-max-h');
      }
    };
  }, [containerRef, drawerHeight, landscape, isOpen]);
};

export default useInterfaceCardHeight;
